import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuiz } from "../../../../context/QuizContext";
import toast from "react-hot-toast";
import axios from "axios";
import PDFDocument from "./PDFDocument";
import { pdf } from "@react-pdf/renderer";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./psychometric.css";

function SubmitForm() {
  const { responses } = useQuiz();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    grade: "",
    school_name: "",
    phone: "",
    alt_phone: "",
    email: "",
    alt_email: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handlePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));
    setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleAlternatePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      alt_phone: value,
    }));
    setErrors((prev) => ({ ...prev, alt_phone: "" }));
  };

  const validateForm = () => {
    let formErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      formErrors.name = "Name is required.";
    }

    if (!formData.grade.trim()) {
      formErrors.grade = "Year of study is required.";
    }

    if (!formData.school_name.trim()) {
      formErrors.school_name = "School Name is required.";
    }

    // Phone validation
    if (!formData.phone.trim()) {
      formErrors.phone = "Phone number is required.";
    } else if (formData.phone.length < 10) {
      formErrors.phone = "Valid phone number is required.";
    }

    // Alternate phone validation (optional, but if present, must be valid)
    if (formData.alt_phone && formData.alt_phone.length < 10) {
      formErrors.alt_phone = "Valid alternate phone number is required.";
    }

    // Email validation
    if (!formData.email.trim()) {
      formErrors.email = "Email is required.";
    } else if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email)
    ) {
      formErrors.email = "Please enter a valid email address.";
    }

    // Alternate Email validation (if present)
    if (
      formData.alt_email &&
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
        formData.alt_email
      )
    ) {
      formErrors.alt_email = "Please enter a valid alternate email address.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const generatePDF = async (data) => {
    const doc = data && <PDFDocument data={data} userDetail={formData} />;
    const blob = await pdf(doc).toBlob();
    return blob;
  };

  const submitForm = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);
      const data = {
        responses: responses,
        user_detail: formData,
      };

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/swot/submitresponses/`,
          data
        );

        if (response.data.result) {
          const pdfBlob = await generatePDF(response.data.result);
          await sendPdf(pdfBlob, response.data.student_detail.id);
          toast.success("Response submitted successfully");

          setFormData({
            name: "",
            grade: "",
            school_name: "",
            phone: "",
            alt_phone: "",
            email: "",
            alt_email: "",
          });

          navigate("/psychometric/thankyou");
        }
      } catch (error) {
        toast.error(`Failed to submit response: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const sendPdf = async (pdfBlob, id) => {
    const data = new FormData();
    const uniqueId = new Date().toISOString().replace(/[-:.TZ]/g, "");
    data.append(
      "pdf_file_path",
      pdfBlob,
      `SWOT_Test_Report_${formData.name.replace(" ", "_")}_${uniqueId}.pdf`
    );
    data.append("student_id", id);

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/swot/dwonloadreport/`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } catch (error) {
      toast.error("Failed to send PDF. Please try again.");
      console.error("Error sending PDF:", error);
    }
  };

  useEffect(() => {
    if (responses.length < 19) {
      navigate("/");
    }
  }, [responses, navigate]);

  return (
    <div className="psych-page">
      <div className="psych-particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>

      <div className="psych-submit">
        {/* Form Section */}
        <div className="psych-form-section">
          <div className="ai-badge" style={{ marginBottom: "0.8rem" }}>
            <span className="ai-dot"></span>
            AI Report Generation
          </div>

          <h1>Well Done! 🎉</h1>
          <p className="sub-text">
            You've successfully completed the assessment. Enter your details below to receive your AI-generated career report.
          </p>

          {/* Form */}
          <form onSubmit={submitForm}>
            <div className="psych-form-grid">
              {/* Name */}
              <div className="psych-field">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              {/* Grade */}
              <div className="psych-field">
                <label>Grade / Level of Study *</label>
                <select
                  name="grade"
                  value={formData.grade}
                  onChange={handleInputChange}
                >
                  <option value="">Select Grade/Level</option>
                  <option value="8th grade or below">8th grade or below</option>
                  <option value="9th">9th</option>
                  <option value="10th">10th</option>
                  <option value="11th">11th</option>
                  <option value="12th">12th</option>
                  <option value="Currently pursuing Graduation">
                    Currently pursuing Graduation
                  </option>
                  <option value="Graduation completed">
                    Graduation completed
                  </option>
                </select>
                {errors.grade && <span className="field-error">{errors.grade}</span>}
              </div>

              {/* School Name */}
              <div className="psych-field full-width">
                <label>School / College Name *</label>
                <input
                  type="text"
                  name="school_name"
                  value={formData.school_name}
                  onChange={handleInputChange}
                  placeholder="Enter school or college name"
                />
                {errors.school_name && <span className="field-error">{errors.school_name}</span>}
              </div>

              {/* Phone */}
              <div className="psych-field">
                <label>Phone No *</label>
                <PhoneInput
                  country={"in"}
                  value={formData.phone || ""}
                  onChange={handlePhoneChange}
                  inputStyle={{
                    width: "100%",
                    height: "42px",
                    borderRadius: "10px",
                    border: `1.5px solid ${errors.phone ? "#f87171" : "rgba(255,255,255,0.1)"}`,
                    background: "rgba(255,255,255,0.04)",
                    color: "#fff",
                    paddingLeft: "50px",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.9rem",
                  }}
                  containerStyle={{ width: "100%" }}
                  buttonStyle={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1.5px solid rgba(255,255,255,0.1)",
                    borderRadius: "10px 0 0 10px",
                  }}
                  dropdownStyle={{
                    background: "#1a1145",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "#fff",
                  }}
                />
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>

              {/* Alternate Phone */}
              <div className="psych-field">
                <label>Parent's Phone No</label>
                <PhoneInput
                  country={"in"}
                  value={formData.alt_phone || ""}
                  onChange={handleAlternatePhoneChange}
                  inputStyle={{
                    width: "100%",
                    height: "42px",
                    borderRadius: "10px",
                    border: `1.5px solid ${errors.alt_phone ? "#f87171" : "rgba(255,255,255,0.1)"}`,
                    background: "rgba(255,255,255,0.04)",
                    color: "#fff",
                    paddingLeft: "50px",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.9rem",
                  }}
                  containerStyle={{ width: "100%" }}
                  buttonStyle={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1.5px solid rgba(255,255,255,0.1)",
                    borderRadius: "10px 0 0 10px",
                  }}
                  dropdownStyle={{
                    background: "#1a1145",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "#fff",
                  }}
                />
                {errors.alt_phone && <span className="field-error">{errors.alt_phone}</span>}
              </div>

              {/* Email */}
              <div className="psych-field">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com"
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>

              {/* Alternate Email */}
              <div className="psych-field">
                <label>Alternate Email</label>
                <input
                  type="email"
                  name="alt_email"
                  value={formData.alt_email}
                  onChange={handleInputChange}
                  placeholder="alternate@example.com"
                />
                {errors.alt_email && <span className="field-error">{errors.alt_email}</span>}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="psych-btn"
              style={{ marginTop: "1.2rem", opacity: loading ? 0.7 : 1 }}
            >
              {loading && (
                <div className="psych-spinner" style={{ width: 18, height: 18, borderWidth: 2 }}></div>
              )}
              {loading ? "Generating Report..." : "Submit & Get AI Report"}
            </button>
          </form>
        </div>

        {/* CTA Sidebar Card */}
        <div className="psych-cta-card">
          <div className="cta-icon">🧠</div>
          <h3>This is Just the Beginning...</h3>
          <p>
            Our <strong style={{ color: "#fff" }}>AI engine</strong> analyzes your responses to provide a quick
            career snapshot. But every student is unique — and deserves more than
            an algorithm.
          </p>
          <div className="cta-highlight">
            <span>✦ Get a personalized career roadmap from certified experts</span>
          </div>
          <p>
            Our experienced counselors go beyond data — they understand your
            dreams, family context, and academic strengths to craft a career
            plan that truly fits <strong style={{ color: "#fff" }}>you</strong>.
          </p>
          <p style={{ color: "#6ee7b7", fontWeight: "600", fontSize: "0.85rem" }}>
            🎓 5000+ students successfully guided
          </p>
          <div className="cta-contact">
            <a href="tel:+918108745275">📞 +91 8108 745 275</a>
            <a href="mailto:info@aaraconsultancy.com">✉ info@aaraconsultancy.com</a>
            <a href="https://aaraconsultancy.com/one-on-one-counseling/" target="_blank" rel="noopener noreferrer" className="psych-btn psych-btn-outline" style={{ marginTop: "0.6rem", fontSize: "0.8rem", padding: "8px 16px", textAlign: "center", justifyContent: "center" }}>
              Book Expert Counseling →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubmitForm;
